import React, { Suspense } from 'react';

import { createBrowserRouter, redirect, RouteObject } from 'react-router-dom';

import { lazyWithRetry } from '@shared/factory';

import { Splash, UnAuthAppLayout } from '@/components/shared';
import PageNotFound from '@/views/container/PageNotFound';
import { isLoggedIn, setDestinationURL } from '@/data/cache';
import localStorage from '@/utils/localStorageUtil';
import { IAuthMeta } from '@/containers/login/Login';
import constants from '@/utils/constants';
import { getLoginRedirectionContext } from '@/utils/LoginRedirectionUtils';
import { RoutePath } from '@/types';

import { splitRoutesByAuth } from './helper';
import { pfbRoutes } from './pfbRoutes';

const { authRoutes, unAuthRoutes } = splitRoutesByAuth(pfbRoutes);

const LazyAuthLayout = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'AuthAppLayout' */ '@/components/shared/authAppLayout/AuthAppLayout.tsx'
		),
	'AuthAppLayout',
);

const router = createBrowserRouter([
	{
		element: (
			<Suspense fallback={<Splash />}>
				<LazyAuthLayout />
			</Suspense>
		),
		children: [...authRoutes] as RouteObject[],
		// TODO: Add initial api calls in the loader after removing initSaga
		loader: async function isUserLoggedIn() {
			const isUserLoggedIn = await isLoggedIn(constants.LOGIN_CHECK_KEY);
			if (!isUserLoggedIn) {
				setDestinationURL();
				return redirect(RoutePath.LOGIN);
			}

			return null;
		},
		errorElement: <PageNotFound />,
	},
	{
		element: <UnAuthAppLayout />,
		children: [...unAuthRoutes] as RouteObject[],
		loader: async function isUserLoggedIn() {
			const { isLoggedIn: isUserLoggedIn = false, groupSelection = false } =
				localStorage.getItem<IAuthMeta>(constants.LOGIN_CHECK_KEY) || {};
			const selectedMID = localStorage.getItem(constants.SELECTED_MID);
			// Adding extra check for `/user-verify` & `/set-password` route as this can be accessed for
			// both authenticated and unauthenticated merchant.
			// Note: We can remove `UnAuthAppLayout` component as we can have route
			// layoutInfo using usePFBRoute
			if (
				window.location.pathname === RoutePath.USER_VERIFY ||
				window.location.pathname === RoutePath.RESET_PASSWORD ||
				getLoginRedirectionContext()
			) {
				return null;
			}

			if (isUserLoggedIn && (groupSelection ? selectedMID : !groupSelection)) {
				return redirect(RoutePath.DASHBOARD);
			}

			return null;
		},
		errorElement: <PageNotFound />,
	},
]);

export default router;
